import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import { Button, Loading, MultiSelect } from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getPrintableDirectorName } from '@commenda/utils';
import { CorporationType, Country, Currency, StakeholderType } from '@prisma/client';
import { KeyedMutator } from 'swr';
import DirectorModal from '../../Director/new/DirectorModal';
import CapTableKS from '../../EquityInfo/CapTable';
import { useEquityFormState } from '../../EquityInfo/hooks/useEquityFormState';
import { useEquityResources } from '../../EquityInfo/hooks/useEquityResources';
import { IncorporationFormHeading } from '../../common';
import CorporateStakeholderModal from '../../common/corporateStakeholder/CorporateStakeholderModal';
import { CorporateShareholderEquitySplitDistribution } from '../../common/corporateStakeholder/EquitySplitDistribution';
import StakeholderOptionsModal from '../../common/corporateStakeholder/StakeholderOptionsModal';
import CompanyValuationInputIN from './CompanyValuationInputIN';
import DirectorForm from './DirectorForm';
import { validateIndianLlpStakeholders } from './validators';

const EquityInfo = ({
  next,
  previous,
  data,
  mutate,
  setNotification,
  token,
  companyId,
  corporationId,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
  token: string;
  companyId: number;
  corporationId: number;
}) => {
  const {
    directorsData,
    corporations,
    capTableTransactions,
    stockClasses,
    mutateDirectors,
    mutateCorporations,
    mutateCapTableTransactions,
    stakeholderOptions,
    isLoading,
    isError,
  } = useEquityResources(companyId, corporationId);

  const {
    equityInfo,
    setEquityInfo,
    selectedStakeholders,
    setSelectedStakeholders,
    setCorporateShareholderToEdit,
    corporateStakeholderModalVisibility,
    directorModalVisibility,
    stakeholderIdsWithError,
    setStakeholderIdsWithError,
    setCapTableTransactionIdsToDelete,
    isSubmitting,
    stakeholderTypeModalVisibility,
    onAddCorporateShareholder,
    onSubmit,
    selectAddedShareholder,
    onChangeShareholder,
    individualShareholderToEdit,
    corporateShareholderToEdit,
    capTableData,
    onEditShareholderDetails,
  } = useEquityFormState({
    capTableTransactions,
    stockClasses,
    directorsData: directorsData?.directors,
    corporations,
    mutateCorporations,
    resetEditModalPresetState: () => {},
    corporationId,
    config: {
      defaultShareCount: 100_000,
      defaultSharePrice: 1,
      currency: Currency.INR,
    },
    onSubmitSuccess: () => {
      mutate();
      mutateCapTableTransactions();
      next?.();
    },
    validateStakeholdersAndGetIdsWithError: validateIndianLlpStakeholders,
    corporationType: CorporationType.LLP,
    incorporationCountry: Country.IN,
  });

  if (isLoading || isError) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-10 max-w-[700px]">
      <IncorporationFormHeading
        text="Company Ownership"
        additionalText="Please provide information about this corporation's shareholders."
      />

      <div className="max-w-[700px]">
        <CompanyValuationInputIN
          equityInfo={equityInfo}
          setEquityInfo={setEquityInfo}
          shareCountPlaceholder="1,00,000"
        />
      </div>

      <div className="max-w-[700px] flex flex-col gap-4 pb-12 border-b">
        <div className="flex items-center gap-2">
          <h3 className="text-base">Capital contributions</h3>
        </div>
        <MultiSelect
          defaultValue={Object.keys(selectedStakeholders || {})}
          id="shareholder-multiselect"
          placeholder="Select shareholders"
          options={
            stakeholderOptions
              ?.map((stakeholder) => {
                return {
                  label: stakeholder.name,
                  value: stakeholder.id,
                };
              })
              ?.sort((a, b) => a.label.localeCompare(b.label)) || []
          }
          onValueChange={onChangeShareholder}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add a shareholder',
            onAddItem: stakeholderTypeModalVisibility.show,
          }}
        />
        <StakeholderOptionsModal
          isShown={stakeholderTypeModalVisibility.visible}
          toggle={stakeholderTypeModalVisibility.hide}
          onShowDirectorModal={directorModalVisibility.show}
          onShowCorporateShareholderModal={() => {
            stakeholderTypeModalVisibility.hide();
            corporateStakeholderModalVisibility.show();
          }}
        />
        <CorporateShareholderEquitySplitDistribution
          selectedStakeholders={selectedStakeholders}
          setSelectedStakeholders={setSelectedStakeholders}
          equityInfo={equityInfo}
          onChangeShareholder={onChangeShareholder}
          onEditShareholderDetails={onEditShareholderDetails}
          stakeholderIdsWithError={Array.from(stakeholderIdsWithError)}
          setNotification={setNotification}
          setCapTableTransactionIdsToDelete={setCapTableTransactionIdsToDelete}
        />
      </div>

      <div className="flex flex-col pt-2">
        <h3 className="text-base mb-4">Ownership Summary</h3>
        <CapTableKS
          data={capTableData}
          showAmount
          isNumberOfSharesApplicable={false}
        />
      </div>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button onClick={onSubmit} disabled={isSubmitting}>
          <span>Continue</span>
          <ProceedIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>
      <DirectorModal
        isOpen={directorModalVisibility.visible}
        director={individualShareholderToEdit}
        setOpen={directorModalVisibility.hide}
        companyDataMutate={mutateDirectors}
        DirectorForm={DirectorForm}
        setNotification={setNotification}
        companyId={companyId}
        corporationId={corporationId}
        token={token}
        workRequestData={data}
        modalConfig={{
          heading: individualShareholderToEdit
            ? 'Edit individual shareholder'
            : 'Add individual shareholder',
          postSave: (dir) => {
            stakeholderTypeModalVisibility.hide();
            selectAddedShareholder({
              type: StakeholderType.INDIVIDUAL,
              id: dir.id,
              name: getPrintableDirectorName(dir),
            });
            setCorporateShareholderToEdit(undefined);
            setStakeholderIdsWithError((prev) => {
              const newSet = new Set(prev);
              newSet.delete(`INDIVIDUAL_${dir.id}`);
              return newSet;
            });
          },
        }}
      />
      <CorporateStakeholderModal
        selectedCorporateStakeholderId={corporateShareholderToEdit}
        setSelectedCorporateStakeholderId={setCorporateShareholderToEdit}
        companyId={companyId}
        isShown={corporateStakeholderModalVisibility.visible}
        toggle={corporateStakeholderModalVisibility.hide}
        postSave={({
          corporationId: corporateStakeholderId,
          corporationName,
        }) =>
          onAddCorporateShareholder({
            corporateStakeholderId,
            corporationName,
          })
        }
        mutate={async () => {
          await mutateCorporations();
        }}
        setNotification={setNotification}
        headerTextOverride={
          corporateShareholderToEdit
            ? 'Edit corporate shareholder'
            : 'Add corporate shareholder'
        }
        DirectorForm={DirectorForm}
        directors={directorsData?.directors || []}
        mutateDirectors={mutateDirectors}
        corporations={corporations || []}
        workRequestData={data}
      />
    </div>
  );
};

export default EquityInfo;
